import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ProjectApplication } from '../../api';
import { ComponentsCard } from '../../components/ComponentsCard';
import { AddFrontendModal } from '../../components/AddFrontendModal';
import { ComponentSettingsModal } from '../../components/ComponentSettingsModal';
import { ComponentEnvironmentCard } from '../../components/ComponentEnvironmentCard';
import { RepositoryAccessCard } from '../../components/project/RepositoryAccessCard';
import { ConfigurationDrawer } from '../../components/project/ConfigurationDrawer';
import { PanelCard, ProjectSection, QuietEmpty, SectionHeader } from '../../components/project/ui';
import { RowActions } from '../../components/primitives';
import { useProject } from './ProjectContext';
import { WordPressRepositoryCard } from './wordpress/WordPressRepositoryCard';

/**
 * RepositoriesSection: where the code comes from and how it is built.
 * Repository access is one line; every component of the environment —
 * the Magento backend and each frontend — is one row of one table, with
 * its build settings and variables behind that row's actions (CO-90,
 * CO-159). The repository, branch and architecture are said there and in
 * the project header, not again in a card of their own.
 */
export function RepositoriesSection() {
  const project = useProject();
  if (project.isWordPress) {
    return (
      <ProjectSection>
        <RepositoryAccessCard projectId={project.projectId} connection={project.gitConnection} access={project.repositoryAccess.data} onChanged={project.repositoryAccess.reload} />
        <WordPressRepositoryCard />
      </ProjectSection>
    );
  }
  return <MagentoRepositories />;
}

function MagentoRepositories() {
  const project = useProject();
  const navigate = useNavigate();
  const { environment, sectionPath } = project;
  // The drawers keep the application they were opened for; the page's own
  // list is preferred so a save elsewhere shows at once.
  const [buildFor, setBuildFor] = useState<ProjectApplication | null>(null);
  const [variablesFor, setVariablesFor] = useState<ProjectApplication | null>(null);
  const [addingFrontend, setAddingFrontend] = useState(false);
  const [accessEditRequest, setAccessEditRequest] = useState(0);
  // Bumped when this page changes a component, so the cards read it again.
  const [componentsReload, setComponentsReload] = useState(0);

  // Frontends are configured after the Magento setup (CO-73): a Magento
  // Headless project can serve several storefronts, each with its own
  // repository, so none of them belongs in the Magento setup wizard.
  const addFrontendBlockedReason = project.setup.complete
    ? null
    : 'Frontends are added once the Magento setup has run on the server. Finish the setup first; then add one frontend per storefront.';
  const openAddFrontend = () => setAddingFrontend(true);
  const configureAccess = () => {
    setAccessEditRequest((n) => n + 1);
    document.querySelector('[data-testid="repository-access"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const frontends = project.applications.filter((application) => application.type === 'frontend');
  const current = (picked: ProjectApplication | null) =>
    picked ? (project.applications.find((application) => application.id === picked.id) ?? picked) : null;
  const buildApplication = current(buildFor);
  const variablesApplication = current(variablesFor);

  return (
    <ProjectSection>
      <RepositoryAccessCard projectId={project.projectId} connection={project.gitConnection} editRequest={accessEditRequest} access={project.repositoryAccess.data} onChanged={project.repositoryAccess.reload} />

      {environment ? (
        <ComponentsCard
          key={`components-${environment.id}`}
          projectId={project.projectId}
          environment={environment}
          canDeploy={project.placedPrimary}
          reloadToken={componentsReload}
          onDeploy={project.openDeploy}
          onOpenDeployment={(id) => navigate(sectionPath('deployments', { deployment: id }))}
          onOpenGroup={(id) => navigate(sectionPath('deployments', { group: id }))}
          onApplicationsChanged={() => {
            project.reloadApplications();
            project.refresh();
          }}
          headless={project.isHeadless}
          addFrontendBlockedReason={project.isHeadless ? addFrontendBlockedReason : null}
          setupPath={sectionPath('setup')}
          onAddFrontend={openAddFrontend}
          onConfigureAccess={configureAccess}
          onVariables={setVariablesFor}
        />
      ) : (
        <>
          <QuietEmpty testId="no-environment">
            No environment · <Link to={sectionPath('settings')}>Add an environment</Link>
          </QuietEmpty>
          {/* Without an environment there are no component cards, so the
              frontends' build settings stay reachable from here. */}
          {project.isHeadless && frontends.length > 0 ? (
            <PanelCard labelledBy="repos-frontends" testId="frontends-without-environment">
              <SectionHeader id="repos-frontends" title={`Frontends (${frontends.length})`} />
              <ul className="repos-frontend-list">
                {frontends.map((application) => (
                  <li key={application.id} className="repos-frontend-list__item">
                    <div className="repos-frontend-list__text">
                      <strong>{application.name}</strong>
                      <code title={application.repository_url}>{application.repository_url}</code>
                    </div>
                    <RowActions>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => setBuildFor(application)}
                        data-testid={`build-settings-${application.slug}`}
                      >
                        Edit build settings
                      </button>
                    </RowActions>
                  </li>
                ))}
              </ul>
            </PanelCard>
          ) : null}
        </>
      )}

      {addingFrontend && environment ? (
        <AddFrontendModal
          projectId={project.projectId}
          environment={environment}
          onClose={() => setAddingFrontend(false)}
          onAdded={() => {
            setAddingFrontend(false);
            setComponentsReload((n) => n + 1);
            project.reloadApplications();
            project.refresh();
          }}
          onConfigureAccess={() => {
            setAddingFrontend(false);
            configureAccess();
          }}
        />
      ) : null}

      {buildApplication ? (
        <ComponentSettingsModal
          projectId={project.projectId}
          application={buildApplication}
          environmentId={environment?.id}
          others={project.applications.filter((application) => application.id !== buildApplication.id)}
          onClose={() => setBuildFor(null)}
          onSaved={() => {
            setBuildFor(null);
            setComponentsReload((n) => n + 1);
            project.reloadApplications();
          }}
        />
      ) : null}

      <ConfigurationDrawer
        open={variablesApplication !== null && environment !== null}
        title={variablesApplication ? `${variablesApplication.name} environment variables` : 'Environment variables'}
        onClose={() => setVariablesFor(null)}
        testId="variables-drawer"
        size="wide"
      >
        {variablesApplication && environment ? (
          <ComponentEnvironmentCard
            key={`${variablesApplication.id}-${environment.id}`}
            projectId={project.projectId}
            applicationId={variablesApplication.id}
            applicationName={variablesApplication.name}
            hideHeading
            environment={environment}
          />
        ) : null}
      </ConfigurationDrawer>
    </ProjectSection>
  );
}
